"use client"

import { useState } from "react"
import { track } from "@vercel/analytics"
import { CalendarDays } from "lucide-react"
import { GhlBookingEmbed, ghlIframeId } from "@/components/ghl/GhlBookingEmbed"
import { Section } from "@/components/marketing/landing/LandingBlocks"
import { useLang } from "@/contexts/language-context"

/**
 * Inscripción a los seminarios en vivo quincenales (spec calendarios GHL §5).
 * Redy no guarda inscritos: GHL manda la confirmación con el link de Zoom y los
 * recordatorios. Lo único que se registra aquí es el clic en "Inscribirme", para
 * saber cuántos llegan desde cada página.
 *
 * El calendario (iframe + form_embed.js) solo carga después de ese clic, para
 * que la página no pague ese peso a quien no lo quiere.
 *
 * Reglas de texto: nada de cifras de ingreso ni "cómo ganar dinero".
 */

export type SeminarAudience = "partners" | "agents"

/** Evento de analítica del clic en "Inscribirme". Exportado para probarlo sin DOM. */
export function trackSeminarSignupClick(audience: SeminarAudience): void {
  track("seminar_signup_click", { audience })
}

/** La vista pura: `open` decide si el calendario ya está a la vista. */
export function SeminarSectionView({
  audience,
  calendarUrl,
  open,
  onSignup,
}: {
  audience: SeminarAudience
  calendarUrl: string | null | undefined
  open: boolean
  onSignup?: () => void
}) {
  const { t } = useLang()
  const src = calendarUrl?.trim()
  if (!src) return null

  const title =
    audience === "partners"
      ? t("How Redy's partner program works", "Cómo funciona el programa de socios de Redy")
      : t("Meet Redy's sales agent program", "Conoce el programa de vendedores de Redy")
  const subtitle =
    audience === "partners"
      ? t(
          "A live session on Zoom every two weeks. We walk you through the program and answer your questions.",
          "Una sesión en vivo por Zoom cada dos semanas. Te explicamos el programa y resolvemos tus dudas."
        )
      : t(
          "A live session on Zoom every two weeks. We show you the product and how it's offered.",
          "Una sesión en vivo por Zoom cada dos semanas. Te mostramos el producto y cómo se ofrece."
        )

  return (
    <Section id="seminario" tone="soft">
      <div className="max-w-[820px] mx-auto text-center">
        <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">
          {t("Live seminar", "Seminario en vivo")}
        </div>
        <h2 className="text-4xl md:text-[44px] font-black tracking-tight leading-[1.08] text-redy-charcoal">{title}</h2>
        <p className="text-lg text-redy-secondary mt-5 leading-relaxed font-medium">{subtitle}</p>

        {open ? (
          <div className="mt-10 text-left rounded-[24px] bg-white border border-black/5 shadow-sm p-2 sm:p-4">
            <GhlBookingEmbed
              src={src}
              iframeId={ghlIframeId(src)}
              title={title}
              fallbackLabel={t("Calendar not loading? Open it in a new tab", "¿No carga el calendario? Ábrelo en otra pestaña")}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={onSignup}
            className="mt-8 group inline-flex items-center justify-center gap-2 px-8 py-4 text-[15px] font-bold rounded-full transition-all active:scale-95 whitespace-nowrap cursor-pointer bg-redy-charcoal text-white shadow-lg hover:bg-black"
          >
            <CalendarDays className="w-4 h-4" aria-hidden />
            {t("Sign me up", "Inscribirme")}
          </button>
        )}
      </div>
    </Section>
  )
}

export function SeminarSection({
  audience,
  calendarUrl,
}: {
  audience: SeminarAudience
  /** Sin URL configurada (env vacía) la sección no existe y la página queda como antes. */
  calendarUrl: string | null | undefined
}) {
  const [open, setOpen] = useState(false)
  return (
    <SeminarSectionView
      audience={audience}
      calendarUrl={calendarUrl}
      open={open}
      onSignup={() => {
        trackSeminarSignupClick(audience)
        setOpen(true)
      }}
    />
  )
}
